import { useCallback, useEffect, useRef, useState } from 'react';
import { DatabaseBackup } from 'lucide-react';
import { getEnv } from '../utils/env';

export type RecoveryOffer = {
  eligible: boolean;
  environment: string;
  reason?: string;
  recovery_mode?: boolean;
  restart_required?: boolean;
  worker_enabled?: boolean;
  confirmation?: string;
  backup?: { archive_path: string; captured_at: string; database_count: number } | null;
};
type Phase = 'idle' | 'restoring' | 'completed' | 'deferred';

async function request(path: string, init?: RequestInit) {
  const base = getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
  const key = getEnv('VITE_AI_API_KEY', '');
  if (!base || !key) throw new Error('De herstelcontrole is niet geconfigureerd.');
  let response: Response;
  try {
    response = await fetch(`${base}/api/v1/maintenance/bootstrap${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', 'X-Bootstrap-API-Key': key },
    });
  } catch {
    throw new Error('Geen verbinding met de API. Controleer of de API bereikbaar is en probeer opnieuw.');
  }
  const payload = await response.json().catch(() => {
    throw new Error('De API gaf geen geldig antwoord op de herstelcontrole. Probeer opnieuw.');
  });
  if (!response.ok) throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Herstelcontrole niet beschikbaar.');
  return payload;
}

export function BootstrapRecoveryPanel({ offer, phase, error, checking, onRestore, onCheck, onLater, onlyWhenRecoveryNeeded = false }: {
  offer: RecoveryOffer | null; phase: Phase; error: string; checking: boolean;
  onRestore: () => void; onCheck: () => void; onLater: () => void;
  onlyWhenRecoveryNeeded?: boolean;
}) {
  const restart = phase === 'completed' || offer?.restart_required;
  const available = !error && offer?.eligible && offer.backup && offer.confirmation;
  if (onlyWhenRecoveryNeeded && !offer?.recovery_mode && !offer?.eligible && !restart) return null;
  if (!offer?.recovery_mode && !available && !restart && !error) return null;
  const reason = offer?.reason;
  const unavailable = reason === 'backup_unavailable'
    ? 'De back-upopslag kon niet worden gecontroleerd. Controleer de verbinding en probeer opnieuw.'
    : reason === 'backup_not_recent'
      ? 'Er is geen back-up van maximaal zeven dagen oud beschikbaar voor deze omgeving.'
      : reason === 'no_backup'
        ? 'Er is geen recente back-up voor deze omgeving gevonden. Je kunt later opnieuw controleren; er wordt niets aangemaakt.'
        : 'De beschikbaarheid van een back-up is nog niet vastgesteld. Controleer opnieuw.';
  return (
    <section className="border-y border-gray-200 py-4 text-left" aria-labelledby="bootstrap-restore-title">
      <div className="flex items-start gap-3">
        <DatabaseBackup className="mt-0.5 shrink-0 text-primary-600" size={20} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="ui-kicker">Omgeving herstellen{offer?.environment ? ` · ${offer.environment}` : ''}</p>
          <h2 id="bootstrap-restore-title" className="mt-1 text-base font-semibold text-gray-900">
            {restart ? 'Herstart vereist' : error ? 'Herstelstatus niet beschikbaar' : available ? 'Recente back-up gevonden' : 'Wachten op herstel'}
          </h2>
          <p className="mt-2 text-sm leading-6 text-gray-600" role="status">
            {restart
              ? 'De databases zijn teruggezet. Herstart de API zodat de opstartcontroles kunnen draaien. Controleer daarna opnieuw.'
              : error
                ? 'De actuele herstelstatus kon niet worden opgehaald. We weten daardoor niet of er een bruikbare back-up is. Controleer opnieuw voordat je verdergaat.'
              : phase === 'restoring'
                ? 'De back-up wordt gecontroleerd en teruggezet. De omgeving blijft gesloten.'
                : phase === 'deferred'
                  ? 'Herstel is uitgesteld. De omgeving blijft gesloten totdat je een back-up terugzet.'
                  : available && offer?.backup
                    ? `Back-up van ${new Date(offer.backup.captured_at).toLocaleString('nl-NL')} met ${offer.backup.database_count} databases. Wil je deze terugzetten? Accounts, wachtwoorden en pincodes blijven behouden.`
                    : unavailable}
          </p>
          {offer?.worker_enabled === false && !restart && <p className="mt-2 text-sm text-gray-600">De taakworker staat uit. Schakel deze in voordat je herstelt.</p>}
          {error && <p className="mt-2 text-sm text-red-700" role="alert">{error}</p>}
          <div className="mt-3 flex flex-wrap gap-3">
            {available && !restart && phase !== 'deferred' && <button type="button" onClick={onRestore}
              disabled={phase === 'restoring' || checking || offer?.worker_enabled === false}
              className="min-h-11 bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-50">
              Back-up terugzetten
            </button>}
            <button type="button" onClick={onCheck} disabled={phase === 'restoring' || checking}
              className="min-h-11 px-2 text-sm font-semibold text-gray-700 disabled:opacity-50">
              {checking ? 'Controleren…' : 'Opnieuw controleren'}
            </button>
            {available && !restart && phase === 'idle' && <button type="button" onClick={onLater}
              className="min-h-11 px-2 text-sm text-gray-600">Later</button>}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function BootstrapRecovery({ onlyWhenRecoveryNeeded = false }: { onlyWhenRecoveryNeeded?: boolean }) {
  const [offer, setOffer] = useState<RecoveryOffer | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const mounted = useRef(true);
  const restoring = useRef(false);
  const check = useCallback(async (afterRestore = false) => {
    setChecking(true);
    setError('');
    try {
      const next: RecoveryOffer = await request('/backup-restore');
      if (typeof next?.eligible !== 'boolean' || typeof next?.environment !== 'string') {
        throw new Error('De API gaf geen geldige herstelstatus. Probeer opnieuw.');
      }
      if (!mounted.current) return;
      if (afterRestore && !next.recovery_mode && next.reason === 'databases_present') {
        window.location.reload();
        return;
      }
      setOffer(next);
      setPhase(next.restart_required ? 'completed' : 'idle');
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : 'Herstelcontrole mislukt.');
    } finally {
      if (mounted.current) setChecking(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    void check();
    return () => { mounted.current = false; };
  }, [check]);

  const restore = async () => {
    if (restoring.current || !offer?.eligible || !offer.backup || !offer.confirmation) return;
    if (!window.confirm(`Back-up van ${new Date(offer.backup.captured_at).toLocaleString('nl-NL')} terugzetten naar ${offer.environment}?`)) return;
    restoring.current = true;
    setPhase('restoring');
    setError('');
    try {
      const task = await request('/backup-restore', { method: 'POST', body: JSON.stringify({
        archive_path: offer.backup.archive_path, confirmation: offer.confirmation,
      }) });
      while (mounted.current) {
        await new Promise(resolve => window.setTimeout(resolve, 1000));
        if (!mounted.current) return;
        const result = await request(`/tasks/${encodeURIComponent(task.id)}`);
        if (result.status === 'completed') { setPhase('completed'); return; }
        if (result.status === 'failed' || result.status === 'not_found') throw new Error(result.error || 'Herstel mislukt. Controleer opnieuw voordat je opnieuw probeert.');
      }
    } catch (cause) {
      if (mounted.current) {
        setPhase('idle');
        setError(cause instanceof Error ? cause.message : 'Herstel mislukt.');
      }
    } finally { restoring.current = false; }
  };
  return <BootstrapRecoveryPanel offer={offer} phase={phase} error={error} checking={checking}
    onlyWhenRecoveryNeeded={onlyWhenRecoveryNeeded}
    onRestore={() => void restore()} onCheck={() => void check(phase === 'completed')}
    onLater={() => setPhase('deferred')} />;
}
